import { useEffect, useMemo, useState } from 'react';
import { Edit, Loader2, Plus, Save, Settings2, Trash2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { AdvancedTable } from '../components/AdvancedTable';
import { vehicleInspectionService } from '../services/vehicleInspectionService';
import { vehicleService } from '../services/vehicleService';
import { trailerService } from '../services/trailerService';
import { driverService } from '../services/driverService';
import { documentService } from '../services/documentService';
import { useAuth } from '../contexts/AuthContext';
import { exportRowsToExcel } from '../utils/excelExport';
import { staticDefinitionService } from '../services/staticDefinitionService';

const DEFAULT_STATIC_OPTIONS: Record<string, string[]> = {
  interiorHygieneStatus: ['Yok', 'Çok Temiz', 'Normal', 'Kirli'],
  bodyCondition: ['Hasar Yok', 'Ön Tampon', 'Ön Panjur/Izgara', 'Kaput', 'Sağ Ön Çamurluk', 'Sol Ön Çamurluk', 'Sağ Ön Far', 'Sol Ön Far', 'Sağ Ön Sinyal/Park Lambası', 'Sol Ön Sinyal/Park Lambası', 'Ön Sis Farı (Sağ)', 'Ön Sis Farı (Sol)', 'Sol Ön Ayna', 'Sağ Ön Ayna', 'Rüzgarlık'],
  cabinSideSections: ['Hasar Yok'],
  upperSection: ['Hasar Yok'],
  trailerSection: ['Hasar Yok'],
  fuelTankDamage: ['Hasar Yok'],
  tireType: ['Yeni', 'Kaplama', 'Karışık'],
};

const STATIC_OPTION_FIELDS = [
  { key: 'interiorHygieneStatus', label: 'Araç İçi Hijyen Durumu' },
  { key: 'bodyCondition', label: 'Kaporta Durumu (Ön Bölüm)' },
  { key: 'cabinSideSections', label: 'Kabin Yan Bölümleri' },
  { key: 'upperSection', label: 'Üst Bölüm' },
  { key: 'trailerSection', label: 'Dorse Bölümü' },
  { key: 'fuelTankDamage', label: 'Yakıt Depoları Hasar Durumu' },
  { key: 'tireType', label: 'Lastik Tipi' },
] as const;

const STATIC_DEFINITION_KEY_MAP: Record<typeof STATIC_OPTION_FIELDS[number]['key'], string> = {
  interiorHygieneStatus: 'vehicle_inspection_interior_hygiene_status',
  bodyCondition: 'vehicle_inspection_body_condition',
  cabinSideSections: 'vehicle_inspection_cabin_side_sections',
  upperSection: 'vehicle_inspection_upper_section',
  trailerSection: 'vehicle_inspection_trailer_section',
  fuelTankDamage: 'vehicle_inspection_fuel_tank_damage',
  tireType: 'vehicle_inspection_tire_type',
};

const FILE_FIELDS = [
  { key: 'dashboardImageDocumentIds', uploadKey: 'dashboardImages', label: 'Kadran Resmi', documentType: 'vehicle-inspection-dashboard-image' },
  { key: 'externalDamageDocumentIds', uploadKey: 'externalDamageImages', label: 'Dış Hasarlı Bölüm Resimleri', documentType: 'vehicle-inspection-external-damage-image' },
  { key: 'cabinInteriorDamageDocumentIds', uploadKey: 'cabinInteriorDamageImages', label: 'Kabin İçi Hasar Resimleri', documentType: 'vehicle-inspection-cabin-interior-damage-image' },
  { key: 'trailerImageDocumentIds', uploadKey: 'trailerImages', label: 'Dorse Resimleri', documentType: 'vehicle-inspection-trailer-image' },
] as const;

type InspectionForm = {
  controlKm: string;
  vehicleId: string;
  vehiclePlate: string;
  trailerId: string;
  trailerPlate: string;
  driverId: string;
  driverName: string;
  inspectorUserId: string;
  inspectorName: string;
  adblueChip: boolean;
  mainFuelChip: boolean;
  fuelProtectionSystem: boolean;
  spareFuelChip: boolean;
  interiorHygieneStatus: string;
  poleCount: string;
  bodyCondition: string;
  cabinSideSections: string;
  upperSection: string;
  trailerSection: string;
  fuelTankDamage: string;
  uttsChip: boolean;
  glove: boolean;
  brush: boolean;
  driverPenaltyPoints: string;
  tireDepth: string;
  fuelKey: boolean;
  tireType: string;
  strapsComplete: boolean;
  poleCountComplete: boolean;
  vehicleDriverComment: string;
  strapCount: string;
  strapNote: string;
  fuelBottomSealNote: string;
  dashboardImageDocumentIds: string[];
  externalDamageDocumentIds: string[];
  cabinInteriorDamageDocumentIds: string[];
  trailerImageDocumentIds: string[];
};

const buildInitialForm = (): InspectionForm => ({
  controlKm: '',
  vehicleId: '',
  vehiclePlate: '',
  trailerId: '',
  trailerPlate: '',
  driverId: '',
  driverName: '',
  inspectorUserId: '',
  inspectorName: '',
  adblueChip: false,
  mainFuelChip: false,
  fuelProtectionSystem: false,
  spareFuelChip: false,
  interiorHygieneStatus: '',
  poleCount: '',
  bodyCondition: '',
  cabinSideSections: '',
  upperSection: '',
  trailerSection: '',
  fuelTankDamage: '',
  uttsChip: false,
  glove: false,
  brush: false,
  driverPenaltyPoints: '',
  tireDepth: '',
  fuelKey: false,
  tireType: '',
  strapsComplete: false,
  poleCountComplete: false,
  vehicleDriverComment: '',
  strapCount: '',
  strapNote: '',
  fuelBottomSealNote: '',
  dashboardImageDocumentIds: [],
  externalDamageDocumentIds: [],
  cabinInteriorDamageDocumentIds: [],
  trailerImageDocumentIds: [],
});

type OptionItem = { id: string; label: string; [key: string]: any };

const buildPayload = (form: InspectionForm) => ({
  adblueChip: form.adblueChip,
  mainFuelChip: form.mainFuelChip,
  fuelProtectionSystem: form.fuelProtectionSystem,
  spareFuelChip: form.spareFuelChip,
  interiorHygieneStatus: form.interiorHygieneStatus,
  poleCount: form.poleCount,
  bodyCondition: form.bodyCondition,
  cabinSideSections: form.cabinSideSections,
  upperSection: form.upperSection,
  trailerSection: form.trailerSection,
  fuelTankDamage: form.fuelTankDamage,
  uttsChip: form.uttsChip,
  glove: form.glove,
  brush: form.brush,
  driverPenaltyPoints: form.driverPenaltyPoints,
  tireDepth: form.tireDepth,
  fuelKey: form.fuelKey,
  tireType: form.tireType,
  strapsComplete: form.strapsComplete,
  poleCountComplete: form.poleCountComplete,
  vehicleDriverComment: form.vehicleDriverComment,
  strapCount: form.strapCount,
  strapNote: form.strapNote,
  fuelBottomSealNote: form.fuelBottomSealNote,
});

export function VehicleInspectionForms() {
  const { user } = useAuth();
  const [rows, setRows] = useState<any[]>([]);
  const [vehicles, setVehicles] = useState<OptionItem[]>([]);
  const [trailers, setTrailers] = useState<OptionItem[]>([]);
  const [drivers, setDrivers] = useState<OptionItem[]>([]);
  const [inspectors, setInspectors] = useState<OptionItem[]>([]);
  const [staticOptions, setStaticOptions] = useState<Record<string, string[]>>(DEFAULT_STATIC_OPTIONS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<any | null>(null);
  const [form, setForm] = useState<InspectionForm>(buildInitialForm());
  const [optionManagerField, setOptionManagerField] = useState<string | null>(null);
  const [optionManagerDefinitionId, setOptionManagerDefinitionId] = useState('');
  const [optionManagerItems, setOptionManagerItems] = useState<Array<{ id?: string; value: string; order: number; isActive: boolean }>>([]);
  const [newOptionValue, setNewOptionValue] = useState('');
  const [uploadFiles, setUploadFiles] = useState<Record<string, File[]>>({});

  const loadData = async () => {
    try {
      setLoading(true);
        const [inspectionRes, vehicleRes, trailerRes, driverRes, staticDefinitionsRes] = await Promise.all([
          vehicleInspectionService.getAll(),
          vehicleService.getVehicles(),
          trailerService.getTrailers(),
          driverService.getDrivers({ type: 'internal' }),
          staticDefinitionService.getAll(),
        ]);

      const rawVehicles = Array.isArray((vehicleRes as any)?.data) ? (vehicleRes as any).data : [];
      setVehicles(rawVehicles.map((item: any) => ({
        id: String(item.id),
        label: item.cekici_plaka || item.plate || '-',
        trailerPlate: item.linked_trailer_plate || item.dorse_plaka || '',
        assignedDriverId: item.assigned_driver_id ? String(item.assigned_driver_id) : '',
        assignedDriverName: item.driver_name || '',
      })));

      const rawTrailers = Array.isArray((trailerRes as any)?.data) ? (trailerRes as any).data : [];
      setTrailers(rawTrailers.map((item: any) => ({ id: String(item.id), label: item.plate || '-' })));

      const rawDrivers = Array.isArray((driverRes as any)?.data) ? (driverRes as any).data : [];
      const mappedDrivers = rawDrivers.map((item: any) => ({
        id: String(item.id),
        label: item.full_name || item.name || '-',
        plate: item.cekici_plaka || '',
        userId: item.user_id ? String(item.user_id) : '',
        isControlResponsible: Number(item.is_control_responsible || 0) === 1,
      }));
      setDrivers(mappedDrivers);
      setInspectors(mappedDrivers.filter((item: any) => item.isControlResponsible));

        setRows(Array.isArray((inspectionRes as any)?.data) ? (inspectionRes as any).data : []);
        const definitions = Array.isArray((staticDefinitionsRes as any)?.data) ? (staticDefinitionsRes as any).data : [];
        const nextOptions = { ...DEFAULT_STATIC_OPTIONS };
        STATIC_OPTION_FIELDS.forEach((field) => {
          const definition = definitions.find((item: any) => item.key === STATIC_DEFINITION_KEY_MAP[field.key]);
          if (definition) {
            nextOptions[field.key] = Array.isArray(definition.items) ? definition.items.map((item: any) => item.value) : [];
          }
        });
        setStaticOptions(nextOptions);
      } catch (error: any) {
      console.error('Vehicle inspection data load failed:', error);
      toast.error(error?.message || 'Araç kontrol formu verileri alınamadı');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, []);

  const currentUserInspector = useMemo(() => inspectors.find((item: any) => String(item.userId || '') === String(user?.id || '')), [inspectors, user?.id]);

  const openCreate = () => {
    setEditingRow(null);
    setUploadFiles({});
    setForm({
      ...buildInitialForm(),
      inspectorUserId: currentUserInspector?.userId || '',
      inspectorName: currentUserInspector?.label || '',
    });
    setModalOpen(true);
  };

  const openEdit = (row: any) => {
    setEditingRow(row);
    setUploadFiles({});
    setForm({
      ...buildInitialForm(),
      ...row,
      controlKm: String(row.controlKm || ''),
      poleCount: String(row.poleCount || ''),
      driverPenaltyPoints: String(row.driverPenaltyPoints || ''),
      tireDepth: String(row.tireDepth || ''),
      strapCount: String(row.strapCount || ''),
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingRow(null);
    setForm(buildInitialForm());
    setUploadFiles({});
  };

  const handleSelectLinkedOption = (field: 'vehicle' | 'trailer' | 'driver' | 'inspector', value: string) => {
    if (field === 'vehicle') {
      const selected = vehicles.find((item) => item.label === value);
      const matchedTrailer = trailers.find((item) => item.label === (selected?.trailerPlate || ''));
      setForm((current) => ({
        ...current,
        vehicleId: selected?.id || '',
        vehiclePlate: value,
        trailerId: matchedTrailer?.id || current.trailerId,
        trailerPlate: selected?.trailerPlate || current.trailerPlate,
        driverId: selected?.assignedDriverId || current.driverId,
        driverName: selected?.assignedDriverName || current.driverName,
      }));
      return;
    }
    if (field === 'trailer') {
      const selected = trailers.find((item) => item.label === value);
      setForm((current) => ({ ...current, trailerId: selected?.id || '', trailerPlate: value }));
      return;
    }
    if (field === 'driver') {
      const selected = drivers.find((item) => item.label === value);
      setForm((current) => ({ ...current, driverId: selected?.id || '', driverName: value }));
      return;
    }
    const selected = inspectors.find((item: any) => item.label === value);
    setForm((current) => ({ ...current, inspectorUserId: selected?.userId || '', inspectorName: value }));
  };

  const openOptionManager = async (fieldKey: string) => {
    try {
      const definitionKey = STATIC_DEFINITION_KEY_MAP[fieldKey as keyof typeof STATIC_DEFINITION_KEY_MAP];
      const response: any = await staticDefinitionService.getByKey(definitionKey);
      setOptionManagerField(fieldKey);
      setOptionManagerDefinitionId(String(response?.data?.id || ''));
      setOptionManagerItems(Array.isArray(response?.data?.items)
        ? response.data.items.map((item: any) => ({ id: item.id, value: item.value, order: item.order, isActive: item.isActive !== false }))
        : []);
      setNewOptionValue('');
    } catch (error: any) {
      console.error('Vehicle inspection options load failed:', error);
      toast.error(error?.message || 'Statik seçenekler alınamadı');
    }
  };

  const saveOptionManager = async () => {
    if (!optionManagerField || !optionManagerDefinitionId) return;
    try {
      for (const item of optionManagerItems) {
        if (!item.value.trim()) continue;
        if (item.id) {
          await staticDefinitionService.updateItem(item.id, { value: item.value.trim(), order: item.order, isActive: item.isActive });
        } else {
          await staticDefinitionService.createItem(optionManagerDefinitionId, { value: item.value.trim(), order: item.order, isActive: item.isActive });
        }
      }
      await loadData();
      setOptionManagerField(null);
      setOptionManagerDefinitionId('');
      setOptionManagerItems([]);
    } catch (error: any) {
      console.error('Vehicle inspection options save failed:', error);
      toast.error(error?.message || 'Statik seçenekler kaydedilemedi');
    }
  };

  const uploadSelectedFiles = async (recordId: string, key: string, documentType: string) => {
    const files = uploadFiles[key] || [];
    if (files.length === 0) return [] as string[];
    const ids: string[] = [];
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entityType', 'vehicle_inspection');
      formData.append('entityId', recordId);
      formData.append('documentType', documentType);
      formData.append('name', file.name);
      const response: any = await documentService.uploadDocument(formData);
      if (response?.data?.id) ids.push(String(response.data.id));
    }
    return ids;
  };

  const buildRequestPayload = (nextForm: InspectionForm) => ({
    vehicleId: nextForm.vehicleId || null,
    vehiclePlate: nextForm.vehiclePlate || null,
    trailerId: nextForm.trailerId || null,
    trailerPlate: nextForm.trailerPlate || null,
    driverId: nextForm.driverId || null,
    driverName: nextForm.driverName || null,
    inspectorUserId: nextForm.inspectorUserId || null,
    inspectorName: nextForm.inspectorName || null,
    controlKm: nextForm.controlKm || null,
    interiorHygieneStatus: nextForm.interiorHygieneStatus || null,
    bodyCondition: nextForm.bodyCondition || null,
    driverPenaltyPoints: nextForm.driverPenaltyPoints || null,
    tireDepth: nextForm.tireDepth || null,
    payload: buildPayload(nextForm),
    dashboardImageDocumentIds: nextForm.dashboardImageDocumentIds,
    externalDamageDocumentIds: nextForm.externalDamageDocumentIds,
    cabinInteriorDamageDocumentIds: nextForm.cabinInteriorDamageDocumentIds,
    trailerImageDocumentIds: nextForm.trailerImageDocumentIds,
  });

  const handleSave = async () => {
    if (!form.vehiclePlate || !form.driverName || !form.inspectorName) {
      toast.error('Çekici, sürücü ve kontrol eden zorunludur');
      return;
    }

    try {
      setSaving(true);
      const basePayload = buildRequestPayload(form);
      if (editingRow) {
        const updateRes: any = await vehicleInspectionService.update(editingRow.id, basePayload);
        if (!updateRes?.success) throw new Error(updateRes?.message || 'Kayıt güncellenemedi');

        const nextForm = { ...form };
        for (const field of FILE_FIELDS) {
          const ids = await uploadSelectedFiles(editingRow.id, field.uploadKey, field.documentType);
          if (ids.length > 0) {
            nextForm[field.key] = [...new Set([...(nextForm[field.key] || []), ...ids])];
          }
        }

        if (JSON.stringify(buildRequestPayload(nextForm)) !== JSON.stringify(basePayload)) {
          await vehicleInspectionService.update(editingRow.id, buildRequestPayload(nextForm));
        }
      } else {
        const createRes: any = await vehicleInspectionService.create(basePayload);
        if (!createRes?.success || !createRes?.data?.id) throw new Error(createRes?.message || 'Kayıt oluşturulamadı');
        const recordId = String(createRes.data.id);

        const nextForm = { ...form };
        for (const field of FILE_FIELDS) {
          const ids = await uploadSelectedFiles(recordId, field.uploadKey, field.documentType);
          if (ids.length > 0) {
            nextForm[field.key] = ids;
          }
        }

        if (JSON.stringify(buildRequestPayload(nextForm)) !== JSON.stringify(basePayload)) {
          await vehicleInspectionService.update(recordId, buildRequestPayload(nextForm));
        }
      }

      toast.success(editingRow ? 'Araç kontrol formu güncellendi' : 'Araç kontrol formu oluşturuldu');
      closeModal();
      await loadData();
    } catch (error: any) {
      console.error('Vehicle inspection save failed:', error);
      toast.error(error?.message || 'Araç kontrol formu kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: any) => {
    if (!window.confirm('Bu kontrol formunu silmek istediğinize emin misiniz?')) return;
    try {
      const response: any = await vehicleInspectionService.delete(row.id);
      if (!response?.success) throw new Error(response?.message || 'Kayıt silinemedi');
      toast.success('Araç kontrol formu silindi');
      await loadData();
    } catch (error: any) {
      console.error('Vehicle inspection delete failed:', error);
      toast.error(error?.message || 'Araç kontrol formu silinemedi');
    }
  };

  const tableData = useMemo(() => rows.map((row) => ({
    id: row.id,
    inspectionNo: row.inspectionNo || row.id,
    vehiclePlate: row.vehiclePlate || '-',
    trailerPlate: row.trailerPlate || '-',
    driverName: row.driverName || '-',
    inspectorName: row.inspectorName || '-',
    controlKm: row.controlKm || '-',
    interiorHygieneStatus: row.interiorHygieneStatus || '-',
    bodyCondition: row.bodyCondition || '-',
    createdAt: row.createdAt ? new Date(row.createdAt).toLocaleString('tr-TR') : '-',
  })), [rows]);

  const tableColumns = useMemo(() => [
    { key: 'inspectionNo', label: 'Kontrol #' },
    { key: 'vehiclePlate', label: 'Çekici Plaka' },
    { key: 'trailerPlate', label: 'Dorse Plaka' },
    { key: 'driverName', label: 'Sürücü' },
    { key: 'inspectorName', label: 'Kontrol Eden' },
    { key: 'controlKm', label: 'Kontrol Kilometresi' },
    { key: 'interiorHygieneStatus', label: 'İç Hijyen' },
    { key: 'bodyCondition', label: 'Kaporta Durumu' },
    { key: 'createdAt', label: 'Kayıt Tarihi' },
    {
      key: 'id',
      label: 'İşlem',
      render: (_value: string, record: any) => {
        const rawRow = rows.find((item) => String(item.id) === String(record.id));
        if (!rawRow) return null;
        return (
          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={(e) => { e.stopPropagation(); openEdit(rawRow); }} className="rounded-lg bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100">
              <Edit className="mr-1 inline h-4 w-4" /> Düzenle
            </button>
            <button type="button" onClick={(e) => { e.stopPropagation(); void handleDelete(rawRow); }} className="rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-100">
              <Trash2 className="mr-1 inline h-4 w-4" /> Sil
            </button>
          </div>
        );
      },
    },
  ], [rows]);

  const renderLinkedField = (label: string, field: 'vehicle' | 'trailer' | 'driver' | 'inspector', value: string, options: OptionItem[], placeholder: string) => (
    <div className="grid grid-cols-1 gap-2 md:grid-cols-[240px_minmax(0,1fr)] md:items-center">
      <label className="text-sm font-semibold text-slate-700">{label}</label>
      <div>
        <input
          list={`vehicle-inspection-${field}-options`}
          value={value}
          onChange={(e) => handleSelectLinkedOption(field, e.target.value)}
          placeholder={placeholder}
          className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm"
        />
        <datalist id={`vehicle-inspection-${field}-options`}>
          {options.map((item) => <option key={item.id} value={item.label}>{item.label}</option>)}
        </datalist>
      </div>
    </div>
  );

  const renderCheckboxField = (label: string, value: boolean, onChange: (checked: boolean) => void) => (
    <div className="grid grid-cols-1 gap-2 md:grid-cols-[240px_minmax(0,1fr)] md:items-center">
      <label className="text-sm font-semibold text-slate-700">{label}</label>
      <label className="inline-flex items-center gap-3">
        <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} className="h-5 w-5 rounded border-gray-300" />
        <span className="text-sm text-slate-600">İşaretle</span>
      </label>
    </div>
  );

  const renderStaticOptionField = (fieldKey: typeof STATIC_OPTION_FIELDS[number]['key'], label: string) => (
    <div className="grid grid-cols-1 gap-2 md:grid-cols-[240px_minmax(0,1fr)_auto] md:items-center">
      <label className="text-sm font-semibold text-slate-700">{label}</label>
      <select
        value={(form as any)[fieldKey] || ''}
        onChange={(e) => setForm((current) => ({ ...current, [fieldKey]: e.target.value }))}
        className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm"
      >
        <option value="">Statik Liste Değeri Ekle</option>
        {(staticOptions[fieldKey] || []).map((item, index) => <option key={`${fieldKey}-${item}-${index}`} value={item}>{item}</option>)}
      </select>
      <button type="button" onClick={() => openOptionManager(fieldKey)} className="rounded-lg border border-gray-300 px-3 py-3 text-sm text-slate-700 hover:bg-gray-50">
        <Settings2 className="inline h-4 w-4" /> Yönet
      </button>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Araç Kontrol Formları</h1>
          <p className="mt-1 text-gray-500">Araç kontrol formlarını doldurun, kaydedin ve yönetin.</p>
        </div>
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => exportRowsToExcel(tableData as any[], `vehicle-inspection-forms-${new Date().toISOString().slice(0, 10)}.xlsx`, { sheetName: 'VehicleInspectionForms' })} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
            Excele Aktar
          </button>
          <button type="button" onClick={openCreate} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">
            <Plus className="mr-2 inline h-4 w-4" /> Yeni Form
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center rounded-xl border border-gray-200 bg-white py-16">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <AdvancedTable
          data={tableData as any}
          columns={tableColumns as any}
          onRefresh={() => void loadData()}
          onRowDoubleClick={(record: any) => {
            const row = rows.find((item) => String(item.id) === String(record.id));
            if (row) openEdit(row);
          }}
        />
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[94vh] w-full max-w-6xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
            <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
              <h2 className="text-xl font-bold text-gray-900">{editingRow ? 'Araç Kontrol Formu Düzenle' : 'Yeni Araç Kontrol Formu'}</h2>
              <button type="button" onClick={closeModal} className="text-gray-400 hover:text-gray-600"><X className="h-6 w-6" /></button>
            </div>
            <div className="space-y-5 p-6">
              <div className="text-3xl font-bold text-slate-900">Tüm alanlar</div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-2 md:grid-cols-[240px_minmax(0,1fr)] md:items-center">
                  <label className="text-sm font-semibold text-slate-700">Kontrol Kilometresi</label>
                  <input type="number" value={form.controlKm} onChange={(e) => setForm((current) => ({ ...current, controlKm: e.target.value }))} placeholder="Sayı Ekle" className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm" />
                </div>

                {renderLinkedField('Çekici Plaka', 'vehicle', form.vehiclePlate, vehicles, 'Bağlantılı liste kaydı bul ve ekle')}
                {renderLinkedField('Dorse Plaka', 'trailer', form.trailerPlate, trailers, 'Bağlantılı liste kaydı bul ve ekle')}
                {renderLinkedField('Sürücü Adı', 'driver', form.driverName, drivers, 'Bağlantılı liste kaydı bul ve ekle')}
                {renderLinkedField('Kontrol Eden', 'inspector', form.inspectorName, inspectors, 'Bağlantılı liste kaydı bul ve ekle')}

                {renderCheckboxField('Adblue Çipi', form.adblueChip, (checked) => setForm((current) => ({ ...current, adblueChip: checked })))}
                {renderCheckboxField('Ana Depo Yakıt Çipi', form.mainFuelChip, (checked) => setForm((current) => ({ ...current, mainFuelChip: checked })))}
                {renderCheckboxField('Yakıt Koruma Sistemi', form.fuelProtectionSystem, (checked) => setForm((current) => ({ ...current, fuelProtectionSystem: checked })))}
                {renderCheckboxField('Yedek Depo Yakıt Çipi', form.spareFuelChip, (checked) => setForm((current) => ({ ...current, spareFuelChip: checked })))}

                {STATIC_OPTION_FIELDS.map((field) => (
                  <div key={field.key}>{renderStaticOptionField(field.key, field.label)}</div>
                ))}

                <div className="grid grid-cols-1 gap-2 md:grid-cols-[240px_minmax(0,1fr)] md:items-center">
                  <label className="text-sm font-semibold text-slate-700">Ştanga Adedi</label>
                  <input type="number" value={form.poleCount} onChange={(e) => setForm((current) => ({ ...current, poleCount: e.target.value }))} placeholder="Sayı Ekle" className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm" />
                </div>

                {FILE_FIELDS.map((field) => (
                  <div key={field.key} className="grid grid-cols-1 gap-2 md:grid-cols-[240px_minmax(0,1fr)] md:items-center">
                    <label className="text-sm font-semibold text-slate-700">{field.label}</label>
                    <label className="flex cursor-pointer items-center justify-between rounded-lg border border-gray-300 px-4 py-3 text-sm text-slate-500 hover:bg-gray-50">
                      <span>Alan seçiniz ve alanları yapıştırınız veya sürükleyiniz</span>
                      <span className="font-medium text-blue-600"><Upload className="mr-1 inline h-4 w-4" /> Yükle</span>
                      <input type="file" multiple accept="image/*" className="hidden" onChange={(e) => setUploadFiles((current) => ({ ...current, [field.uploadKey]: Array.from(e.target.files || []) }))} />
                    </label>
                    <div className="md:col-start-2 text-xs text-gray-500">
                      Mevcut: {(form[field.key] || []).length} dosya{(uploadFiles[field.uploadKey] || []).length > 0 ? ` • Yeni: ${(uploadFiles[field.uploadKey] || []).map((file) => file.name).join(', ')}` : ''}
                    </div>
                  </div>
                ))}

                {renderCheckboxField('UTTS Çipi', form.uttsChip, (checked) => setForm((current) => ({ ...current, uttsChip: checked })))}
                {renderCheckboxField('Eldiven', form.glove, (checked) => setForm((current) => ({ ...current, glove: checked })))}
                {renderCheckboxField('Fırça', form.brush, (checked) => setForm((current) => ({ ...current, brush: checked })))}

                <div className="grid grid-cols-1 gap-2 md:grid-cols-[240px_minmax(0,1fr)] md:items-center">
                  <label className="text-sm font-semibold text-slate-700">Sürücü Ceza Puanı</label>
                  <input type="number" value={form.driverPenaltyPoints} onChange={(e) => setForm((current) => ({ ...current, driverPenaltyPoints: e.target.value }))} placeholder="Sayı Ekle" className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm" />
                </div>

                <div className="grid grid-cols-1 gap-2 md:grid-cols-[240px_minmax(0,1fr)] md:items-center">
                  <label className="text-sm font-semibold text-slate-700">Lastik Derinlik</label>
                  <input type="number" step="0.01" value={form.tireDepth} onChange={(e) => setForm((current) => ({ ...current, tireDepth: e.target.value }))} placeholder="Sayı Ekle" className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm" />
                </div>

                {renderCheckboxField('Yakıt Anahtarı', form.fuelKey, (checked) => setForm((current) => ({ ...current, fuelKey: checked })))}
                {renderCheckboxField('Spanzetler Tam', form.strapsComplete, (checked) => setForm((current) => ({ ...current, strapsComplete: checked })))}
                {renderCheckboxField('Ştanga Adedi Tam', form.poleCountComplete, (checked) => setForm((current) => ({ ...current, poleCountComplete: checked })))}

                {[
                  { key: 'vehicleDriverComment', label: 'Araç ve Sürücü Açıklama' },
                  { key: 'strapCount', label: 'Spanzet Adedi' },
                  { key: 'strapNote', label: 'Spanzet Not' },
                  { key: 'fuelBottomSealNote', label: 'Yakıt Alt Tapa Mühür Notu' },
                ].map((field) => (
                  <div key={field.key} className="grid grid-cols-1 gap-2 md:grid-cols-[240px_minmax(0,1fr)] md:items-center">
                    <label className="text-sm font-semibold text-slate-700">{field.label}</label>
                    <textarea value={(form as any)[field.key] || ''} onChange={(e) => setForm((current) => ({ ...current, [field.key]: e.target.value }))} placeholder="Metin Ekle" rows={2} className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm" />
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
                <button type="button" onClick={closeModal} className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50">İptal</button>
                <button type="button" onClick={() => void handleSave()} disabled={saving} className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-60">
                  {saving ? <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> : <Save className="mr-2 inline h-4 w-4" />} Kaydet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {optionManagerField && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">{STATIC_OPTION_FIELDS.find((item) => item.key === optionManagerField)?.label} seçeneklerini yönet</h3>
              <button type="button" onClick={() => { setOptionManagerField(null); setOptionManagerDefinitionId(''); setOptionManagerItems([]); }} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-4 p-6">
              <div className="flex gap-3">
                <input value={newOptionValue} onChange={(e) => setNewOptionValue(e.target.value)} placeholder="Yeni seçenek ekle" className="flex-1 rounded-lg border border-gray-300 px-4 py-3 text-sm" />
                <button
                  type="button"
                  onClick={() => {
                    const value = newOptionValue.trim();
                    if (!value) return;
                    setOptionManagerItems((current) => [...current, { value, order: current.length + 1, isActive: true }]);
                    setNewOptionValue('');
                  }}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
                >
                  Ekle
                </button>
              </div>

              <div className="max-h-[420px] space-y-3 overflow-y-auto">
                {optionManagerItems.map((item, index) => (
                  <div key={`${item.id || 'new'}-${index}`} className="grid grid-cols-[minmax(0,1fr)_90px_auto] items-center gap-3">
                    <input value={item.value} onChange={(e) => setOptionManagerItems((current) => current.map((option, optionIndex) => optionIndex === index ? { ...option, value: e.target.value } : option))} className="rounded-lg border border-gray-300 px-4 py-3 text-sm" />
                    <input type="number" value={item.order} onChange={(e) => setOptionManagerItems((current) => current.map((option, optionIndex) => optionIndex === index ? { ...option, order: Number(e.target.value) || 0 } : option))} className="rounded-lg border border-gray-300 px-4 py-3 text-sm" />
                    <button type="button" onClick={async () => {
                      try {
                        if (item.id) {
                          await staticDefinitionService.deleteItem(item.id);
                          await loadData();
                        }
                        setOptionManagerItems((current) => current.filter((_, optionIndex) => optionIndex !== index));
                      } catch (error: any) {
                        console.error('Vehicle inspection option delete failed:', error);
                        toast.error(error?.message || 'Statik seçenek silinemedi');
                      }
                    }} className="rounded-lg bg-red-50 px-3 py-3 text-red-700 hover:bg-red-100">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
                <button type="button" onClick={() => { setOptionManagerField(null); setOptionManagerDefinitionId(''); setOptionManagerItems([]); }} className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50">İptal</button>
                <button type="button" onClick={() => void saveOptionManager()} className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700">Kaydet</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
